import React, { useState, useEffect } from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { handlePaymentSuccess } from './Components/StripeCheckout';
import BottomNav from './Layout/BottomNav';
import HomePage from './Home/HomePage';
import Journey from './Journey/Journey';
import LoginPage from './Auth/LoginPage';
import MorePage from './More/MorePage';
import LibraryPage from './Pages/LibraryPage';
import ResetPasswordPage from './Pages/ResetPasswordPage';
import { supabase } from '../src/shared/supabase';
import ProgressPage from './Progress/ProgressPage';
import JournalHistory from './Journey/JournalHistory';
import SessionHistory from './Journey/SessionHistory';
import AdminDashboard from './Admin/AdminDashboard';

import { 
  isNativePlatform, 
  isAndroid,
  addBackButtonListener, 
  setStatusBarLight,
  setStatusBarColor,
  logPlatformInfo,
  exitApp
} from './shared/utils/platform';

// ✅ URL to tab mapping
const TAB_ROUTES = {
  '/':          'home',
  '/home':      'home',
  '/session':   'session',
  '/journal':   'journal',
  '/progress':  'progress',
  '/library':   'library',
  '/more':      'more',
};

const ROUTE_FROM_TAB = {
  'home':     '/home',
  'session':  '/session',
  'journal':  '/journal',
  'progress': '/progress',
  'library':  '/library',
  'more':     '/more',
};

// ✅ Inner component uses router hooks
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ activeTab is derived from URL automatically
  const activeTab = TAB_ROUTES[location.pathname] || 'home';

  const [sessionActive, setSessionActive] = useState(false);
  const [sessionProgress, setSessionProgress] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // ✅ Tab change now updates the URL
  const setActiveTab = (tab) => {
    navigate(ROUTE_FROM_TAB[tab] || '/home');
  };

  useEffect(() => {
    initializeCapacitor();
  }, []);

  const initializeCapacitor = async () => {
    if (isNativePlatform()) {
      console.log('📱 Running on native platform');
      await logPlatformInfo();
      await setStatusBarLight();
      await setStatusBarColor('#FFFFFF');
      
      if (isAndroid()) {
        setupBackButtonHandler();
      }
    } else {
      console.log('🌐 Running on web platform');
    }
  };

  const setupBackButtonHandler = async () => {
    const listener = await addBackButtonListener((event) => {
      console.log('⬅️ Back button pressed on:', activeTab);
      
      if (showAuthModal) {
        setShowAuthModal(false);
        return;
      }
      
      if (activeTab === 'home') {
        exitApp();
      } else if (activeTab === 'session' && sessionActive) {
        const confirmExit = window.confirm('Are you sure you want to exit this session? Your progress will be saved.');
        if (confirmExit) {
          setSessionActive(false);
          setActiveTab('home');
        }
      } else {
        setActiveTab('home');
      }
    });

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  };

  useEffect(() => {
    checkAuth();
    
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      setUser(session?.user || null);
      if (session?.user) {
        fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
      }
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      const sessionId = urlParams.get('session_id');
      
      if (paymentStatus === 'success' && sessionId && user) {
        console.log('💳 Processing payment success...');
        
        const success = await handlePaymentSuccess(sessionId, user.id);
        
        if (success) {
          alert('🎉 Welcome to Premium! You now have unlimited access!');
          await fetchUserProfile(user.id);
          navigate('/home');
        }
      } else if (paymentStatus === 'cancelled') {
        alert('Payment cancelled. You can upgrade anytime from your profile.');
        navigate('/home');
      }
    };
    
    if (user) {
      checkPaymentStatus();
    }
  }, [user]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setIsAuthenticated(!!session);
    setUser(session?.user || null);
    
    if (session?.user) {
      await fetchUserProfile(session.user.id);
    }
  };

  const fetchUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      setUserProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleStartSession = (journeyId) => {
    if (!isAuthenticated) {
      sessionStorage.setItem('selectedJourneyId', journeyId);
      setActiveTab('session');
      return;
    }
    
    sessionStorage.setItem('selectedJourneyId', journeyId);
    sessionStorage.removeItem('resumeSessionId');
    sessionStorage.removeItem('resumeStep');
    setActiveTab('session');
    setSessionActive(true);
    setSessionProgress(0);
  };

  const handleResumeSession = (journeyId) => {
    console.log('🔄 Resume session called with journey:', journeyId);
    
    setActiveTab('session');
    setSessionActive(true);
    
    const resumeStep = sessionStorage.getItem('resumeStep');
    if (resumeStep === 'chat') {
      setSessionProgress(33);
    } else if (resumeStep === 'journaling' || resumeStep === 'journal') {
      setSessionProgress(66);
    } else if (resumeStep === 'meditation') {
      setSessionProgress(90);
    }
  };

  const handleSessionComplete = () => {
    setSessionActive(false);
    setSessionProgress(100);
    setActiveTab('progress');
    sessionStorage.removeItem('selectedJourneyId');
    sessionStorage.removeItem('resumeSessionId');
    sessionStorage.removeItem('resumeStep');
  };

  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
    setShowAuthModal(false);
    checkAuth();
    
    const intendedJourney = sessionStorage.getItem('selectedJourneyId');
    if (intendedJourney && activeTab === 'session') {
      setSessionActive(true);
    } else {
      sessionStorage.removeItem('selectedJourneyId');
    }
  };

  const handleShowAuth = () => {
    console.log('🔐 Opening auth modal...');
    setShowAuthModal(true);
  };

  const handleNavigateToMore = () => {
    setActiveTab('more');
  };

  const handleNavigateToLibrary = () => {
    setActiveTab('library');
  };

  const renderContent = () => {
    // ✅ Special routes - handled inside renderContent
    if (location.pathname === '/reset-password') {
      return <ResetPasswordPage />;
    }

    if (location.pathname === '/admin') {
      return <AdminDashboard />;
    }

    if (showAuthModal && !isAuthenticated) {
      return (
        <>
          {activeTab === 'library' && (
            <LibraryPage 
              isAuthenticated={isAuthenticated}
              user={user}
              userProfile={userProfile}
              onNavigate={setActiveTab}
              onShowAuth={handleShowAuth}
            />
          )}
          {activeTab === 'home' && (
            <HomePage 
              onStartSession={handleStartSession}
              onResumeSession={handleResumeSession}
              onNavigateToProgress={() => setActiveTab('progress')}
              onNavigateToMore={handleNavigateToMore}
              isAuthenticated={isAuthenticated}
              user={user}
              userProfile={userProfile}
            />
          )}
          <div className="fixed inset-0 z-50 bg-black/50">
            <LoginPage 
              onAuthSuccess={handleAuthSuccess}
              onClose={() => setShowAuthModal(false)}
            />
          </div>
        </>
      );
    }

    if (activeTab === 'session' && !isAuthenticated) {
      return <LoginPage onAuthSuccess={handleAuthSuccess} />;
    }

    switch(activeTab) {
      case 'home':
        return (
          <HomePage 
            onStartSession={handleStartSession}
            onResumeSession={handleResumeSession}
            onNavigateToProgress={() => setActiveTab('progress')}
            onNavigateToMore={handleNavigateToMore}
            onNavigateToLibrary={handleNavigateToLibrary}
            isAuthenticated={isAuthenticated}
            user={user}
            userProfile={userProfile}
          />
        );
      
      case 'session':
        const selectedJourney = sessionStorage.getItem('selectedJourneyId');
        const resumeSession = sessionStorage.getItem('resumeSessionId');
        
        console.log('🎯 Session case - selectedJourney:', selectedJourney, 'resumeSession:', resumeSession, 'sessionActive:', sessionActive);
        
        if (selectedJourney && (sessionActive || resumeSession)) {
          return (
            <Journey 
              onProgressUpdate={setSessionProgress}
              onComplete={handleSessionComplete}
              onCancel={() => {
                setSessionActive(false);
                setActiveTab('home');
              }}
              user={user}
              userProfile={userProfile}
            />
          );
        } else {
          sessionStorage.removeItem('selectedJourneyId');
          sessionStorage.removeItem('resumeSessionId');
          sessionStorage.removeItem('resumeStep');
          return (
            <SessionHistory 
              onStartNewSession={() => setActiveTab('home')}
              user={user}
              userProfile={userProfile}
              onNavigate={setActiveTab}
            />
          );
        }
      
      case 'journal':
        return (
          <JournalHistory 
            user={user}
            userProfile={userProfile}
            onNavigate={setActiveTab}
          />
        );

      case 'more':
        return (
          <MorePage 
            isAuthenticated={isAuthenticated}
            user={user}
            userProfile={userProfile}
            onNavigate={setActiveTab}
            onShowAuth={handleShowAuth}
          />
        );
      
      case 'progress':
        return (
          <ProgressPage 
            user={user} 
            userProfile={userProfile} 
            onNavigate={setActiveTab}
          />
        );
      
      case 'library':
        return (
          <LibraryPage 
            isAuthenticated={isAuthenticated}
            user={user}
            userProfile={userProfile}
            onNavigate={setActiveTab}
            onShowAuth={handleShowAuth}
          />
        );
      
      default:
        return (
          <HomePage 
            onStartSession={handleStartSession}
            onResumeSession={handleResumeSession}
            onNavigateToProgress={() => setActiveTab('progress')}
            onNavigateToLibrary={handleNavigateToLibrary}
            onNavigateToMore={handleNavigateToMore}
            isAuthenticated={isAuthenticated}
            user={user}
            userProfile={userProfile}
          />
        );
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-peach-50 via-white to-cream-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-peach-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-transparent border-t-orange-500 border-r-orange-400 rounded-full animate-spin"></div>
          </div>
          <div className="text-gray-800 text-lg font-medium">Loading your journey...</div>
          <div className="text-gray-500 text-sm mt-2">✨ Preparing your experience</div>
        </div>
      </div>
    );
  }

  // ✅ Hide bottom nav on special routes
  const hideNav = ['/reset-password', '/admin'].includes(location.pathname);

  return (
    <div className="app-container">
      {renderContent()}
      {!hideNav && (
        <BottomNav
          activeTab={activeTab}
          onTabChange={setActiveTab}
          sessionActive={sessionActive}
          sessionProgress={sessionProgress}
        />
      )}
    </div>
  );
}

// ✅ Wrap with BrowserRouter
function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;